import { CarouselSection, ChartSection, LoadingAlert } from '@/components';
import { useYieldBearingAssetRawData } from '@/components/providers';
import { CarouselScreenProps, YieldBearingAssetInterface } from '@/interfaces';
import { formatYieldDataForChart } from '@/utils/formatUtils';
import { logError, logInfo } from '@/utils/logger';
import { YIELD_BEARING_ASSET } from '@/utils/strings';
import { FC, useEffect, useState } from 'react';
interface AggregatedData {
  timestamp: string;
  value: number;
}
interface HistoricalDataPoint {
  timestamp: string;
  tvlUsd: number;
  apy: number;
}
interface YieldBearingAssetTvlAndReturnCarouselSectionProps {
  asset: YieldBearingAssetInterface;
  customCompositions?: Array<{ composition: number }>;
  showForcedLoadingIndicatorBetweenChartUpdates?: boolean;
}
const FORCED_DELAY_AFTER_DATA_LOAD_TO_PREVENT_ANIMATION_FLICKER_IN_MS = 400;
const processChartData = (
  aggregatedData: Array<{ timestamp: string; value: number }>,
): AggregatedData[] =>
  formatYieldDataForChart(aggregatedData, 'value').map((data) => ({
    timestamp: data.time,
    value: data.value,
    label: data.label,
  }));
const buildCarouselScreens = (
  aggregatedTvlData: AggregatedData[],
  aggregatedReturnData: AggregatedData[],
  currentApy: number,
  ticker: string,
): CarouselScreenProps[] => [
  {
    mainHeader: YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE.RETURN_TITLE,
    chipLabel:
      YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE.RETURN_CHIP_LABEL,
    body: (
      <ChartSection
        chartingData={formatYieldDataForChart(aggregatedReturnData, 'value')}
        chartId={`chart-apy-${ticker}`}
        yAxisType="percentage"
        noDataNode={<div>No APY data available for this asset.</div>}
        isBackgroundTransparent={true}
        isFullBleed={true}
        startAtZero
      />
    ),
  },
  {
    mainHeader:
      YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE
        .MARKET_CAPITALIZATION_TITLE,
    chipLabel:
      YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE
        .MARKET_CAPITALIZATION_CHIP_LABEL,
    body: (
      <ChartSection
        chartingData={formatYieldDataForChart(aggregatedTvlData, 'value')}
        chartId={`chart-tvl-${ticker}`}
        noDataNode={<div>No TVL data available for this asset.</div>}
        isBackgroundTransparent={true}
        isFullBleed={true}
        startAtZero
      />
    ),
  },
];
const YieldBearingAssetTvlAndReturnCarouselSection: FC<
  YieldBearingAssetTvlAndReturnCarouselSectionProps
> = ({
  asset,
  customCompositions,
  showForcedLoadingIndicatorBetweenChartUpdates = true,
}) => {
  const { fetchAssetHistoricalData } = useYieldBearingAssetRawData();
  const [tvlData, setTvlData] = useState<AggregatedData[]>([]);
  const [returnData, setReturnData] = useState<AggregatedData[]>([]);
  const [carouselScreens, setCarouselScreens] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const fetchAndProcessData = async () => {
      try {
        setLoading(true);
        logInfo(
          'Fetching historical data for yield-bearing asset...',
          'YieldBearingAssetTvlAndReturnCarouselSection',
          {
            ticker: asset.ticker,
          },
        );
        const aggregatedData = await fetchAssetHistoricalData(asset.ticker);
        if (aggregatedData) {
          const aggregatedTvlData = aggregatedData.map(
            (data: HistoricalDataPoint) => ({
              timestamp: data.timestamp,
              value: data.tvlUsd,
            }),
          );
          const aggregatedReturnData = aggregatedData.map(
            (data: HistoricalDataPoint) => ({
              timestamp: data.timestamp,
              value: data.apy,
            }),
          );
          setTvlData(processChartData(aggregatedTvlData));
          setReturnData(processChartData(aggregatedReturnData));
          setCarouselScreens(
            buildCarouselScreens(
              aggregatedTvlData,
              aggregatedReturnData,
              asset.apy,
              asset.ticker,
            ),
          );
          logInfo(
            'Historical data fetched and processed successfully.',
            'YieldBearingAssetTvlAndReturnCarouselSection',
          );
        } else {
          throw new Error('No data returned');
        }
      } catch (err) {
        logError(err, 'YieldBearingAssetTvlAndReturnCarouselSection');
        setError('Failed to fetch historical data for this asset.');
      } finally {
        if (showForcedLoadingIndicatorBetweenChartUpdates) {
          await new Promise((resolve) =>
            setTimeout(
              resolve,
              FORCED_DELAY_AFTER_DATA_LOAD_TO_PREVENT_ANIMATION_FLICKER_IN_MS,
            ),
          );
        }
        setLoading(false);
      }
    };
    fetchAndProcessData();
  }, [
    asset,
    fetchAssetHistoricalData,
    showForcedLoadingIndicatorBetweenChartUpdates,
  ]);
  if (loading) {
    return (
      <LoadingAlert
        title={`${YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE.LOADING_TITLE}`}
        description={`${YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE.LOADING_DESCRIPTION.replace(
          '[yield_bearing_asset_ticker]',
          asset.ticker,
        )}`}
      />
    );
  }
  if (error) {
    logError(error, 'YieldBearingAssetTvlAndReturnCarouselSection');
    return null;
  }
  return <CarouselSection screens={carouselScreens} useChipNavigation={true} />;
};
export default YieldBearingAssetTvlAndReturnCarouselSection;
